import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Download, InsertDriveFileOutlined } from "@mui/icons-material";
import moment from "moment";
import { toast } from "react-toastify";

import { FileService } from "../../services";
import { getDisplayName } from "../../utils/user.util";

const TASK_STATUS_BADGE = {
  DONE: { label: "Done", color: "#2E7D32", bg: "#E8F5E9" },
  INPROGRESS: { label: "In Progress", color: "#EF6C00", bg: "#FFF3E0" },
  TODO: { label: "Todo", color: "#1565C0", bg: "#E3F2FD" },
};

// Downloads a same/cross-origin file via blob so the browser saves it instead of just navigating to it.
const downloadFile = async (url: string, filename: string) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename || "download";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    toast.error("Failed to download file");
    console.error("downloadFile error:", error);
  }
};

export const TaskProgressHistoryModal = ({ open, task, progressList = [], onClose }: any) => {
  const [loadingDocId, setLoadingDocId] = useState<number | null>(null);

  // Documents are private — fetch a fresh 5-minute signed URL only when the user actually opens one.
  const handleOpenDocument = async (progress: any) => {
    const fileId = progress?.uploadedFile?.id;
    if (!fileId) return;

    setLoadingDocId(progress.id);
    try {
      const file = await FileService.getFileDetails(fileId);
      if (file?.url) {
        window.open(file.url, "_blank", "noopener,noreferrer");
      } else {
        toast.error("Unable to open document");
      }
    } catch (error) {
      toast.error("Failed to open document");
      console.error("handleOpenDocument error:", error);
    } finally {
      setLoadingDocId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Progress History — {task?.title}</DialogTitle>
      <DialogContent dividers>
        {progressList?.length > 0 ? (
          <Stack spacing={0}>
            {progressList.map((progress, index) => {
              const statusBadge = TASK_STATUS_BADGE[progress.status] || {
                label: progress.status,
                color: "#616161",
                bg: "#F0F0F0",
              };
              const attachment = progress.uploadedFile;
              const isImage = attachment?.mimeType?.startsWith("image/");
              const isLast = index === progressList.length - 1;

              return (
                <Box key={progress.id} sx={{ position: "relative", pl: 3, pb: isLast ? 0 : 3 }}>
                  {/* Timeline dot */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: 5,
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: "#674BF5",
                    }}
                  />
                  {/* Timeline connector */}
                  {!isLast && (
                    <Box sx={{ position: "absolute", left: 4, top: 15, bottom: 0, width: "2px", bgcolor: "#E0E0E0" }} />
                  )}

                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                    <Chip
                      label={statusBadge.label}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: 11,
                        fontWeight: 600,
                        color: statusBadge.color,
                        backgroundColor: statusBadge.bg,
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      by {progress.updatedBy ? getDisplayName(progress.updatedBy) : "Unknown"}
                    </Typography>
                  </Stack>

                  {progress.notes && (
                    <Typography variant="body2" sx={{ mb: 1, color: "#444" }}>
                      {progress.notes}
                    </Typography>
                  )}

                  {attachment && (
                    <Box sx={{ mb: 1 }}>
                      {isImage ? (
                        attachment.url ? (
                          <Box sx={{ position: "relative", width: "fit-content" }}>
                            <Box
                              component="img"
                              src={attachment.url}
                              alt={attachment.originalName}
                              onClick={() => window.open(attachment.url, "_blank", "noopener,noreferrer")}
                              sx={{
                                width: 110,
                                height: 110,
                                objectFit: "cover",
                                borderRadius: 2,
                                border: "1px solid #EEE",
                                cursor: "pointer",
                              }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => downloadFile(attachment.url, attachment.originalName)}
                              sx={{
                                position: "absolute",
                                bottom: 4,
                                right: 4,
                                bgcolor: "rgba(255,255,255,0.9)",
                                width: 24,
                                height: 24,
                                "&:hover": { bgcolor: "#fff" },
                              }}
                            >
                              <Download sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              width: 110,
                              height: 110,
                              borderRadius: 2,
                              border: "1px dashed #DDD",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "#FAFAFA",
                            }}
                          >
                            <Typography variant="caption" color="text.disabled" sx={{ textAlign: "center", px: 1 }}>
                              Preview unavailable
                            </Typography>
                          </Box>
                        )
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={
                            loadingDocId === progress.id ? (
                              <CircularProgress size={14} />
                            ) : (
                              <InsertDriveFileOutlined sx={{ fontSize: 16 }} />
                            )
                          }
                          onClick={() => handleOpenDocument(progress)}
                          disabled={loadingDocId === progress.id}
                          sx={{ textTransform: "none", borderColor: "#674BF5", color: "#674BF5", fontSize: 12 }}
                        >
                          {attachment.originalName}
                        </Button>
                      )}
                    </Box>
                  )}

                  <Typography variant="caption" color="text.disabled">
                    {moment(progress.createdAt).format("DD MMM YYYY, HH:mm")}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            No progress updates yet
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
